/**
 * Phased loading indicator for the TMD briefing fetch.
 *
 * The briefing takes ~10s end-to-end (Gemini ranking dominates). A single
 * spinner makes this feel broken. Instead, we show the user what's actually
 * happening on the server, stepping through phases on a wall-clock timer
 * whose thresholds match the measured phase lengths from 2026-04-23 perf
 * work. If the server is slow, the last phase ("Finalizing...") sits
 * indefinitely rather than lying that progress is done.
 *
 * The timer is intentionally a lightweight fake — the API isn't streaming
 * real progress, but the phase labels are honest and the timing is usually
 * within a second of reality.
 */
import React, { useEffect, useState } from "react";
import { makeStyles, tokens, Spinner } from "@fluentui/react-components";
import {
  CheckmarkCircle20Filled,
  Circle20Regular,
} from "@fluentui/react-icons";

export interface Phase {
  label: string;
  /** Cumulative ms from load start when this phase is expected to finish. */
  endAtMs: number;
}

// Phase timings chosen from the 2026-04-23 perf investigation (Mike Natale,
// 7 ranked opps: buckets 670ms, Gemini ranking 7s, notes 1.4s, close-date 850ms).
// "Finalizing" has no end time — it sits until the server responds.
const PHASES: Phase[] = [
  { label: "Fetching your opportunities", endAtMs: 1000 },
  { label: "Ranking priorities with Gemini", endAtMs: 8000 },
  { label: "Gathering context notes", endAtMs: 9500 },
  { label: "Analyzing close-date signals", endAtMs: 10500 },
  { label: "Finalizing your briefing", endAtMs: Number.POSITIVE_INFINITY },
];

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: tokens.spacingVerticalXXL,
    gap: tokens.spacingVerticalL,
  },
  title: {
    fontSize: "15px",
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    fontSize: "12px",
    color: tokens.colorNeutralForeground3,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    minWidth: "320px",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    fontSize: "14px",
  },
  pending: {
    color: tokens.colorNeutralForeground3,
  },
  active: {
    color: tokens.colorNeutralForeground1,
    fontWeight: 600,
  },
  done: {
    color: tokens.colorNeutralForeground2,
  },
  iconWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "20px",
    height: "20px",
    flexShrink: 0,
  },
  doneIcon: {
    color: tokens.colorPaletteGreenForeground1,
  },
});

export interface TmdBriefingProgressProps {
  /** Header line. Defaults to the My-Day briefing copy. */
  title?: string;
  /** Sub-line under the title. Pass "" to hide it. */
  subtitle?: string;
  /** Phase steps + timings. Defaults to the ~10s Gemini-ranked briefing phases;
   *  callers with a different (e.g. deterministic, no-Gemini) wait MUST pass
   *  honest phases rather than borrow the briefing's. */
  phases?: Phase[];
}

export const TmdBriefingProgress: React.FC<TmdBriefingProgressProps> = ({
  title = "Loading Tackle My Day",
  subtitle = "This usually takes about 10 seconds.",
  phases = PHASES,
}) => {
  const styles = useStyles();
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const start = performance.now();
    const id = window.setInterval(() => {
      setElapsed(performance.now() - start);
    }, 150);
    return () => window.clearInterval(id);
  }, []);

  // Find the current active phase — the first one whose endAtMs is past elapsed.
  const activeIdx = phases.findIndex((p) => elapsed < p.endAtMs);
  const currentIdx = activeIdx === -1 ? phases.length - 1 : activeIdx;

  return (
    <div className={styles.root}>
      <div className={styles.title}>{title}</div>
      {subtitle !== "" && <div className={styles.subtitle}>{subtitle}</div>}
      <div className={styles.list}>
        {phases.map((p, i) => {
          const state = i < currentIdx ? "done" : i === currentIdx ? "active" : "pending";
          return (
            <div
              key={p.label}
              className={`${styles.row} ${
                state === "done" ? styles.done : state === "active" ? styles.active : styles.pending
              }`}
            >
              <div className={styles.iconWrap}>
                {state === "done" && (
                  <CheckmarkCircle20Filled className={styles.doneIcon} />
                )}
                {state === "active" && <Spinner size="tiny" />}
                {state === "pending" && <Circle20Regular />}
              </div>
              <span>{p.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TmdBriefingProgress;
